"use client";

import { useEffect, useRef, useState } from "react";
import AppImage from "@/components/ui/AppImage";

const costs = [
  {
    icon: "⭐",
    title: "Perte d\'avis 5★",
    desc: "Chaque avis 4★ au lieu de 5★ coûte en moyenne 8% de visibilité sur les plateformes.",
    cost: "−8% visibilité",
    color: "#C9A84C",
  },
  {
    icon: "⏱️",
    title: "Temps perdu",
    desc: "Un gestionnaire sans process perd 2h/jour à répondre à des questions récurrentes.",
    cost: "−2h/jour",
    color: "#D97706",
  },
  {
    icon: "🔄",
    title: "Turn-over équipe",
    desc: "L'absence de documentation force chaque nouveau collaborateur à tout réapprendre.",
    cost: "3 sem. d\'onboarding",
    color: "#DC2626",
  },
  {
    icon: "📉",
    title: "Taux d\'occupation",
    desc: "Les conciergeries non structurées perdent en moyenne 12% de taux d\'occupation vs. concurrentes.",
    cost: "−12% occupation",
    color: "#7C3AED",
  },
];

export default function SolutionSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="solution"
      ref={sectionRef}
      className="py-28 md:py-40 px-6 md:px-16 relative overflow-hidden"
      style={{ background: "var(--charcoal)" }}
    >
      {/* Background decoration */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at 80% 50%, rgba(201,168,76,0.08) 0%, transparent 60%)",
        }}
      />
      <div className="max-w-8xl mx-auto relative z-10">
        {/* Header */}
        <div
          className={`flex items-center gap-6 mb-16 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <span className="font-serif italic text-sm" style={{ color: "var(--gold)" }}>
            Page 10
          </span>
          <div className="gold-line" />
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.4)" }}>
            Transition vers la solution
          </span>
        </div>

        {/* Main title */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-20">
          <div className="lg:col-span-7">
            <h2
              className={`font-serif text-white leading-tight mb-8 transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ fontSize: "clamp(2.2rem, 4.5vw, 4rem)" }}
            >
              Le coût du désordre pour les conciergeries{" "}
              <span className="italic" style={{ color: "var(--gold-light)" }}>
                15+ logements.
              </span>
            </h2>
            <p
              className={`text-base leading-relaxed transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ color: "rgba(255,255,255,0.6)", maxWidth: "520px" }}
            >
              L'absence de structure n'est pas gratuite. Elle a un coût réel, mesurable, qui s'accumule chaque mois. Voici ce que vous perdez concrètement sans les 4 piliers.
            </p>
          </div>
        </div>

        {/* Cost cards */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20 transition-all duration-700 delay-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          {costs?.map((item, i) => (
            <div
              key={i}
              className={`rounded-2xl p-8 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.07)",
                transitionDelay: `${0.3 + i * 0.1}s`,
              }}
            >
              <div className="text-3xl mb-4">{item?.icon}</div>
              <h3 className="font-bold text-white text-sm mb-3">{item?.title}</h3>
              <p className="text-xs leading-relaxed mb-6" style={{ color: "rgba(255,255,255,0.5)" }}>
                {item?.desc}
              </p>
              <div
                className="text-sm font-bold px-3 py-2 rounded-full text-center"
                style={{ background: `${item?.color}20`, color: item?.color, border: `1px solid ${item?.color}40` }}
              >
                {item?.cost}
              </div>
            </div>
          ))}
        </div>

        {/* CTA Block */}
        <div
          className={`rounded-2xl overflow-hidden transition-all duration-700 delay-400 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{ border: "1px solid rgba(201,168,76,0.3)" }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Left: Cover image */}
            <div className="relative h-80 lg:h-auto overflow-hidden">
              <AppImage
                src="/assets/images/Ajouter_un_titre-1771683848983.jpg"
                alt="Guide GîteZen Pro — couverture du guide de gestion de conciergerie structurée"
                fill
                className="object-cover object-center"
              />
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(135deg, rgba(28,25,23,0.6) 0%, rgba(28,25,23,0.2) 100%)" }}
              />
              <div className="absolute bottom-8 left-8">
                <div
                  className="inline-block text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full"
                  style={{ background: "rgba(201,168,76,0.3)", border: "1px solid rgba(201,168,76,0.5)", color: "#E8C97A" }}
                >
                  Guide 2026
                </div>
              </div>
            </div>

            {/* Right: CTA content */}
            <div
              className="p-12 flex flex-col justify-center"
              style={{ background: "rgba(201,168,76,0.05)" }}
            >
              <h3
                className="font-serif italic text-3xl text-white mb-6 leading-tight"
              >
                Prêt à structurer votre conciergerie ?
              </h3>
              <p className="text-sm leading-relaxed mb-8" style={{ color: "rgba(255,255,255,0.65)" }}>
                GîteZen Pro vous accompagne dans la mise en place des 4 piliers. Standardisation, centralisation, automatisation et pilotage qualité — tout en un outil conçu spécifiquement pour les conciergeries 10+ logements.
              </p>

              <div className="space-y-4 mb-10">
                {[
                  "Process Book automatiquement structuré par logement",
                  "Séquences de messages voyageurs prêtes à l'emploi",
                  "Dashboard KPIs en temps réel",
                  "Support dédié conciergeries professionnelles",
                ]?.map((feature, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm" style={{ color: "rgba(255,255,255,0.75)" }}>
                    <span
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: "var(--gold)", color: "var(--charcoal)" }}
                    >
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    {feature}
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://gitezen.pro/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-block text-center"
                  style={{ background: "linear-gradient(135deg, var(--gold), var(--gold-light))", color: "var(--charcoal)" }}
                  aria-label="Découvrir GîteZen Pro"
                >
                  Découvrir GîteZen Pro →
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Final message */}
        <div
          className={`mt-16 text-center transition-all duration-700 delay-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <p
            className="font-serif italic text-xl"
            style={{ color: "rgba(255,255,255,0.4)", maxWidth: "600px", margin: "0 auto" }}
          >
            "La professionnalisation n'est pas une option pour les conciergeries qui veulent durer. C'est leur seul avantage compétitif durable."
          </p>
        </div>
      </div>
    </section>
  );
}